import React, { useEffect, useMemo, useState } from "react";
import { EditModal } from "../EditModal";
import InputV2 from "@appComponents/InputV2";
import { use2FAFieldLogic } from "../../hooks/use2FAFieldLogic";
import { Input } from "antd";
import EmailIcon from "@assets/images/email-svg.svg";
import ProfileService from "@services/Profile.service";

type EmailModalProps = {
  isOpen: boolean;
  currentEmail?: string;
  shouldDisplaySuccessContent?: boolean;
  onClose?: () => void;
  onSave?: (email: string, password: string, otpCode: string) => void;
};

const SuccessContent = ({ email }: { email?: string }) => {
  const handleResend = () => {
    if (email) {
      ProfileService.resendConfirmationEmailChangeEmail(email);
    }
  };

  return (
    <div className="flex flex-col justify-between items-center">
      <div className="w-full">
        <h2 className="text-xl font-semibold ">Check your inbox</h2>
        <p>
          A confirmation email will be sent to your new email address. You must
          confirm the change for it to take effect.
        </p>
      </div>

      <EmailIcon className="my-[20px] lg:my-[40px]" />

      <div>
        Didn't get the email?{" "}
        <a onClick={handleResend} className="font-semibold underline">
          Resend email
        </a>
      </div>
    </div>
  );
};

export const EmailModal = ({
  isOpen,
  currentEmail,
  shouldDisplaySuccessContent,
  onClose,
  onSave,
}: EmailModalProps) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const {
    inputRefs: twoFAInputRefs,
    pin: twoFAPin,
    handleChange: twoFAHandleChange,
    handleKeyDown: twoFAHandleKeyDown,
    handleClearPin: twoFAHandleClearPin,
    handlePaste: twoFAHandlePaste,
  } = use2FAFieldLogic();

  const isButtonDisabled = useMemo(
    () => !email || !password || twoFAPin?.join("").length !== 6,
    [email, password, twoFAPin]
  );

  const handleTwoFAEnterKeyPress = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    twoFAHandleKeyDown(index, e);
    if (index === 5 && e.key === "Enter" && !isButtonDisabled) {
      onSave?.(email, password, twoFAPin?.join(""));
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setEmail("");
      setPassword("");
      twoFAHandleClearPin();
    }
  }, [isOpen]);

  return (
    <EditModal
      onSave={() => onSave?.(email, password, twoFAPin?.join(""))}
      open={isOpen}
      onClose={onClose}
      customSaveBtnText="Request Email Change"
      title="Submit email change request"
      isOnSaveDisabled={isButtonDisabled}
      successContent={<SuccessContent email={email} />}
      shouldDisplaySuccess={shouldDisplaySuccessContent}
    >
      <InputV2
        className="mb-4"
        type="email"
        disabled
        value={currentEmail}
        onChange={(e) => setEmail(e.target.value)}
        label="Current email"
        placeholder="eg. john@doe.com"
        autoComplete="off"
      />

      <InputV2
        className="mb-4"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        label="New email address"
        placeholder="eg. john@doe.com"
        autoComplete="off"
      />

      <div className="text-xs my-8 text-grey-lightest">
        We require your password and 2FA code for security reasons
      </div>

      <InputV2
        className="mb-4"
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        label="Password"
        placeholder="******"
        autoComplete="off"
      />

      <div className="flex flex-col gap-2 mt-8">
        <div className="text-[16px] font-semibold">
          Enter a valid 2FA token to unlock access
        </div>
        <div className="flex gap-2">
          {twoFAPin.map((digit, index) => (
            <Input
              key={index}
              ref={(el) => (twoFAInputRefs.current[index] = el)}
              value={digit}
              maxLength={1}
              onChange={(e) => twoFAHandleChange(index, e.target.value)}
              onKeyDown={(e) => handleTwoFAEnterKeyPress(index, e)}
              onPaste={twoFAHandlePaste}
              className="text-center text-lg font-semibold border border-grey-lighter tailwind-2fa-input"
            />
          ))}
        </div>
      </div>
    </EditModal>
  );
};
